import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Loader2, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { resolvePaymentReturnTarget } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

const FINAL_PAYMENT_STATES = new Set([
  "complete",
  "failed",
  "cancelled",
  "invalid_token",
  "missing_transaction",
]);

function toLocalRoute(redirectUrl) {
  const fallback = "/billing";
  const rawUrl = String(redirectUrl || "").trim();
  if (!rawUrl) {
    return fallback;
  }
  try {
    const parsed = new URL(
      rawUrl,
      typeof window !== "undefined" ? window.location.origin : "https://www.min.cool",
    );
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return rawUrl.startsWith("/") ? rawUrl : fallback;
  }
}

export default function PaymentReturn() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { tx } = useI18n();
  const token = useMemo(() => String(searchParams.get("token") || "").trim(), [searchParams]);
  const [retryKey, setRetryKey] = useState(0);
  const [state, setState] = useState({
    status: token ? "confirming" : "invalid",
    attempts: 0,
    error: "",
  });

  useEffect(() => {
    if (!token) {
      navigate("/billing?payment=invalid_token", { replace: true });
      return undefined;
    }

    let cancelled = false;
    let timeoutId;

    async function pollPayment() {
      setState((current) => ({
        ...current,
        status: current.error ? "retrying" : "confirming",
        attempts: current.attempts + 1,
        error: "",
      }));

      try {
        const payload = await resolvePaymentReturnTarget(token);
        if (cancelled) {
          return;
        }
        const paymentState = String(payload?.payment || "pending").trim();
        if (FINAL_PAYMENT_STATES.has(paymentState)) {
          navigate(toLocalRoute(payload?.redirectUrl), { replace: true });
          return;
        }
        setState((current) => ({
          ...current,
          status: "pending",
          error: "",
        }));
        timeoutId = window.setTimeout(pollPayment, 1400);
      } catch (error) {
        if (cancelled) {
          return;
        }
        setState((current) => ({
          ...current,
          status: "waiting",
          error: error instanceof Error ? error.message : "Could not check payment yet.",
        }));
        timeoutId = window.setTimeout(pollPayment, 2500);
      }
    }

    void pollPayment();

    return () => {
      cancelled = true;
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [navigate, retryKey, token]);

  const hasError = Boolean(state.error);
  const isLongWait = state.attempts >= 8;

  return (
    <main className="min-h-screen bg-slate-50 px-5 py-10 text-slate-950">
      <section className="mx-auto flex min-h-[calc(100vh-5rem)] w-full max-w-sm flex-col items-center justify-center text-center">
        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
          {hasError ? (
            <AlertCircle className="h-6 w-6" aria-hidden="true" />
          ) : (
            <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
          )}
        </div>
        <h1 className="text-2xl font-semibold tracking-normal">{tx("Confirming payment")}</h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">
          {tx("PayFast sent you back. We are checking your upgrade and will move you on as soon as it is confirmed.")}
        </p>
        {hasError ? (
          <p className="mt-4 text-sm leading-6 text-rose-700">{state.error}</p>
        ) : null}
        {isLongWait ? (
          <Button
            type="button"
            variant="outline"
            className="mt-6"
            onClick={() => {
              setState({ status: "confirming", attempts: 0, error: "" });
              setRetryKey((value) => value + 1);
            }}
          >
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
            {tx("Check again")}
          </Button>
        ) : null}
      </section>
    </main>
  );
}
